/**
 * Weather Response Parser — parses AWC API responses.
 * Extracted from example/services/aviation-weather/ResponseParser.ts.
 * Platform-agnostic: pure data transformations.
 */
import type { ParsedMetar, ParsedTaf, TafForecastGroup } from '../types';
import { determineFlightCategory } from './flight-category';

// ── Raw AWC API types (kept minimal for portability) ────────────────────
export interface RawMetarResponse {
  icaoId: string;
  reportTime: string;
  lat: number; lon: number;
  wdir: number | null; wspd: number | null; wgst: number | null;
  visib: number | string | null;
  ceil: number | null;
  temp: number | null; dewp: number | null;
  altim: number | null;
  wxString: string | null;
  rawOb: string;
  fltCat: string | null;
}

export interface RawTafResponse {
  icaoId: string;
  issueTime: string;
  validTimeFrom: string; validTimeTo: string;
  lat: number; lon: number;
  rawTAF: string;
  fcsts: Array<{
    timeFrom: string; timeTo: string;
    changeIndicator: string | null;
    probability: number | null;
    wdir: number | null; wspd: number | null; wgst: number | null;
    visib: number | string | null;
    ceil: number | null;
    wxString: string | null;
  }>;
}

// ── Helpers ─────────────────────────────────────────────────────────────
function safeNum(v: number | string | null | undefined, fallback = 0): number {
  if (v == null) return fallback;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : fallback;
}
function safeStr(v: string | null | undefined): string { return v ?? ''; }
function safeDate(v: string | null | undefined): number {
  if (!v) return Date.now();
  const d = new Date(v).getTime();
  return Number.isFinite(d) ? d : Date.now();
}
function mapChangeIndicator(raw: string | null | undefined): TafForecastGroup['changeIndicator'] {
  const s = (raw ?? '').toUpperCase();
  if (s.startsWith('FM')) return 'FM';
  if (s.startsWith('TEMPO')) return 'TEMPO';
  if (s.startsWith('BECMG')) return 'BECMG';
  if (s.startsWith('PROB')) return 'PROB';
  return 'INITIAL';
}

// ── Public Parsers ──────────────────────────────────────────────────────
export function parseMetars(raw: RawMetarResponse[]): ParsedMetar[] {
  return raw.map(r => {
    const ceiling = r.ceil != null ? safeNum(r.ceil) : null;
    const visibility = safeNum(r.visib, 10);
    return {
      stationId: safeStr(r.icaoId),
      observationTime: safeDate(r.reportTime),
      latitude: safeNum(r.lat),
      longitude: safeNum(r.lon),
      windDirection: safeNum(r.wdir),
      windSpeed: safeNum(r.wspd),
      windGust: r.wgst != null ? safeNum(r.wgst) : null,
      visibility,
      ceiling,
      temperature: safeNum(r.temp),
      dewpoint: safeNum(r.dewp),
      altimeter: safeNum(r.altim, 29.92),
      weatherPhenomena: r.wxString ? r.wxString.split(/\s+/).filter(Boolean) : [],
      flightCategory: determineFlightCategory(visibility, ceiling),
      rawText: safeStr(r.rawOb),
    };
  });
}

export function parseTafs(raw: RawTafResponse[]): ParsedTaf[] {
  return raw.map(r => ({
    stationId: safeStr(r.icaoId),
    issueTime: safeDate(r.issueTime),
    validFrom: safeDate(r.validTimeFrom),
    validTo: safeDate(r.validTimeTo),
    latitude: safeNum(r.lat),
    longitude: safeNum(r.lon),
    forecastGroups: (r.fcsts ?? []).map(f => ({
      changeIndicator: mapChangeIndicator(f.changeIndicator),
      probability: f.probability ?? null,
      fromTime: safeDate(f.timeFrom),
      toTime: safeDate(f.timeTo),
      windDirection: safeNum(f.wdir),
      windSpeed: safeNum(f.wspd),
      windGust: f.wgst != null ? safeNum(f.wgst) : null,
      visibility: safeNum(f.visib, 10),
      ceiling: f.ceil != null ? safeNum(f.ceil) : null,
      weatherPhenomena: f.wxString ? f.wxString.split(/\s+/).filter(Boolean) : [],
    })),
    rawText: safeStr(r.rawTAF),
  }));
}
